import { useEffect } from 'react'
import { useMatches } from '@tanstack/react-router'
import { useBreadcrumbs, type BreadcrumbItem } from '@/contexts/breadcrumb-context'

/**
 * Breadcrumb configuration for routes
 * Maps route paths to their breadcrumb labels and parent routes
 */
const breadcrumbConfig: Record<string, { label: string; parent?: string }> = {
  // Home section routes
  '/home': { label: 'Home' },
  '/dashboard': { label: 'Dashboard', parent: 'Home' },
  '/calendar': { label: 'Calendar', parent: 'Home' },
  '/leads': { label: 'Leads', parent: 'Home' },
  '/pipeline': { label: 'Pipeline', parent: 'Home' },
  '/events': { label: 'Events', parent: 'Home' },
  '/tasks': { label: 'Tasks', parent: 'Home' },
  '/invoices': { label: 'Invoices', parent: 'Home' },
  '/workflows': { label: 'Workflows', parent: 'Home' },

  '/inbox': { label: 'Inbox', parent: 'Home' },

  // CRM section routes
  '/crm': { label: 'CRM' },
  '/contacts': { label: 'Contacts', parent: 'CRM' },
  '/companies': { label: 'Companies', parent: 'CRM' },
  '/marketing': { label: 'Marketing', parent: 'CRM' },

  // Settings section routes
  '/settings': { label: 'Settings' },
  '/company': { label: 'Company', parent: 'Settings' },
  '/team': { label: 'Team & Roles', parent: 'Settings' },
  '/services': { label: 'Services & Pricing', parent: 'Settings' },
  '/email': { label: 'Email', parent: 'Settings' },
  '/forms': { label: 'Inquiry Form', parent: 'Settings' },
  '/client-portal': { label: 'Client Portal', parent: 'Settings' },
  '/communications': { label: 'Email', parent: 'Settings' },
  '/email-suppressions': { label: 'Blocked Recipients', parent: 'Settings' },
  '/notifications': { label: 'Notifications', parent: 'Settings' },
  '/payments': { label: 'Payments', parent: 'Settings' },
  '/integrations': { label: 'Integrations', parent: 'Settings' },
  '/billing': { label: 'Billing', parent: 'Settings' },
  '/settings/workflows': { label: 'Workflows', parent: 'Settings' },
  '/settings/workflows/new': { label: 'Create workflow', parent: 'Settings' },
}

/**
 * Custom hook to automatically set breadcrumbs based on current route
 *
 * This hook eliminates the need for manual breadcrumb management in each route component.
 * It automatically generates breadcrumbs based on the current route path and configuration.
 *
 * @param customBreadcrumbs - Optional custom breadcrumbs to override automatic generation
 *
 * @example
 * // Automatic breadcrumbs based on route
 * function MyPage() {
 *   useBreadcrumbFromRoute()
 *   return <div>Content</div>
 * }
 *
 * @example
 * // Custom breadcrumbs
 * function MyPage() {
 *   useBreadcrumbFromRoute([
 *     { label: 'Custom', href: '/custom' },
 *     { label: 'Page' }
 *   ])
 *   return <div>Content</div>
 * }
 */
export function useBreadcrumbFromRoute(customBreadcrumbs?: BreadcrumbItem[]) {
  const matches = useMatches()
  const { setBreadcrumbs } = useBreadcrumbs()

  useEffect(() => {
    if (customBreadcrumbs) {
      setBreadcrumbs(customBreadcrumbs)
      return
    }

    // Get the last matched route (current page)
    const currentMatch = matches[matches.length - 1]
    if (!currentMatch) return

    const pathname = currentMatch.pathname
    // Normalize pathname by removing trailing slash for matching
    const normalizedPathname = pathname.replace(/\/$/, '') || '/'
    const config = breadcrumbConfig[normalizedPathname] || breadcrumbConfig[pathname]

    if (!config) {
      // Fallback: generate breadcrumb from pathname
      const segments = pathname.split('/').filter(Boolean)
      const label = segments[segments.length - 1]

      // Check if the last segment looks like a UUID, if so use parent
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(label)

      if (isUUID && segments.length > 1) {
        // Build breadcrumb trail from path segments
        const breadcrumbs: BreadcrumbItem[] = []

        // Determine the root section based on path
        const firstSegment = segments[0]
        if (['contacts', 'companies', 'marketing'].includes(firstSegment)) {
          breadcrumbs.push({ label: 'CRM', href: '/crm' })
        } else if (['company', 'team', 'services', 'email', 'forms', 'communications', 'notifications', 'payments', 'integrations', 'billing'].includes(firstSegment)) {
          breadcrumbs.push({ label: 'Settings', href: '/settings' })
        } else {
          breadcrumbs.push({ label: 'Home', href: '/home' })
        }

        // Add parent segments
        for (let i = 0; i < segments.length - 1; i++) {
          const segment = segments[i]
          const formattedLabel = segment
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')

          breadcrumbs.push({
            label: formattedLabel,
            href: '/' + segments.slice(0, i + 1).join('/')
          })
        }

        // Add generic label for the UUID page (singularize the parent)
        const parentLabel = segments[segments.length - 2]
        let singularLabel: string
        if (parentLabel.endsWith('ies')) {
          // companies -> Company, categories -> Category
          singularLabel = parentLabel.slice(0, -3) + 'y'
        } else if (parentLabel.endsWith('s')) {
          // contacts -> Contact, events -> Event
          singularLabel = parentLabel.slice(0, -1)
        } else {
          singularLabel = parentLabel
        }
        singularLabel = singularLabel.charAt(0).toUpperCase() + singularLabel.slice(1)

        breadcrumbs.push({ label: singularLabel })

        setBreadcrumbs(breadcrumbs)
        return
      }

      const formattedLabel = label
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      setBreadcrumbs([{ label: formattedLabel }])
      return
    }

    // Build breadcrumb trail
    const breadcrumbs: BreadcrumbItem[] = []

    // Add parent if specified with appropriate href
    if (config.parent) {
      const parentHref = config.parent === 'Home' ? '/home'
        : config.parent === 'CRM' ? '/crm'
        : config.parent === 'Settings' ? '/settings'
        : undefined

      breadcrumbs.push({ label: config.parent, href: parentHref })
    }

    // Add current page
    breadcrumbs.push({ label: config.label })

    setBreadcrumbs(breadcrumbs)
  }, [matches, customBreadcrumbs, setBreadcrumbs])
}
